import { Box, Col, Flex, Text, type TextWeight, theme } from '@/components';
import type { DiffPath } from '@/types/diff-view-config';
import { BigCalculator, DIFF_DEFAULT_FONT_SIZE } from '@/utils';
import {
  type ComponentType,
  Fragment,
  type PropsWithChildren,
  useMemo,
} from 'react';
import { LegendText } from './LegendText';
import { DiffVisualColors, getReferenceBorderStyle } from './diffFeature';

type DiffViewerContentColsPropsBase<T> = {
  legend: string;
  textConverter?: (value: any, model?: T | null) => string | null;
  isMonetaryField?: boolean;
  isLongField?: boolean;
  isAlignRightField?: boolean;
  showLegendAnytime?: { source: boolean; target: boolean };
  isReferenceTarget?: boolean;
  isSpitedSource?: boolean;
  isLastField?: boolean;
  source: T | null | undefined; // left side
  target: T | null | undefined; // right side
  sourceContentWrapper?: ComponentType<PropsWithChildren>;
  hasValue?: boolean;
  enableForceSourceBackgroundColor?: boolean;
  disableDiffBackgroundColor?: boolean;
};

type InferDiffViewerContentColsProps<T> = DiffViewerContentColsPropsBase<T> & {
  prop: DiffPath<T>;
};

const isFlatProp = <T extends object>(prop: unknown): prop is keyof T => {
  return typeof prop === 'string' && !prop.includes('.');
};

const defaultTextStyle = {
  style: { fontSize: DIFF_DEFAULT_FONT_SIZE },
  weight: 'regular' as TextWeight,
};

export const DiffViewerContentCols = <T extends object | undefined>({
  legend,
  prop,
  textConverter,
  isMonetaryField = false,
  isLongField,
  isAlignRightField = false,
  showLegendAnytime = { target: false, source: false },
  isReferenceTarget,
  isSpitedSource,
  isLastField,
  source,
  target,
  hasValue = true,
  sourceContentWrapper: SourceContentWrapper = Fragment,
  enableForceSourceBackgroundColor,
  disableDiffBackgroundColor = false,
}: InferDiffViewerContentColsProps<T>) => {
  const { token } = theme.useToken();
  const dottedBorderStyle = getReferenceBorderStyle(token.colorInfoBorder);

  const getValue = (diffObject: T | null | undefined) => {
    if (typeof prop !== 'string' || !diffObject) {
      return;
    }
    if (isFlatProp(prop)) {
      return diffObject[prop];
    }
    const splitProp = prop.split('.');
    if (splitProp.some((prop) => !prop)) {
      return;
    }
    return splitProp.reduce((acc, prop) => acc?.[prop], diffObject);
  };

  const sourceValue = getValue(source);
  const targetValue = getValue(target);

  const monetaryDiff: number = useMemo(() => {
    const isNumber = (arg: any): arg is number => {
      return typeof arg === 'number';
    };
    if (!isMonetaryField || !source || !target) {
      return 0;
    }
    if (!isNumber(sourceValue) || !isNumber(targetValue)) {
      return 0;
    }
    return BigCalculator.subtract(sourceValue, targetValue);
  }, [isMonetaryField, source, sourceValue, target, targetValue]);

  const sourceColor =
    monetaryDiff > 0 ? 'primary' : monetaryDiff < 0 ? 'error' : undefined;
  const sourceText =
    textConverter && sourceValue !== undefined && sourceValue !== null
      ? textConverter(sourceValue, source)
      : (sourceValue?.toString() ?? null);
  const targetText =
    textConverter && targetValue !== undefined && targetValue !== null
      ? textConverter(targetValue, target)
      : (targetValue?.toString() ?? null);

  return (
    <>
      <Col
        span={12}
        style={{
          padding: '0 3px',
          backgroundColor:
            enableForceSourceBackgroundColor ||
            (sourceText !== targetText &&
              !isSpitedSource &&
              !disableDiffBackgroundColor)
              ? DiffVisualColors.add
              : undefined,
          ...(isSpitedSource
            ? {
                borderInline: dottedBorderStyle,
                borderBottom: isLastField ? dottedBorderStyle : undefined,
              }
            : {}),
        }}
      >
        {source ? (
          <SourceContentWrapper>
            <Box padding="4px 0">
              {isLongField ? (
                <Flex vertical>
                  <Text {...defaultTextStyle} color="secondary" text={legend} />
                  <Text
                    {...defaultTextStyle}
                    color={sourceColor}
                    text={sourceText}
                  />
                </Flex>
              ) : (
                <LegendText
                  {...defaultTextStyle}
                  textJustify={
                    isMonetaryField || isAlignRightField
                      ? 'flex-end'
                      : undefined
                  }
                  legend={legend}
                  text={sourceText}
                  color={sourceColor}
                />
              )}
            </Box>
          </SourceContentWrapper>
        ) : (
          showLegendAnytime.source && (
            <SourceContentWrapper>
              <Box paddingTop={8} paddingBottom={8}>
                <Text {...defaultTextStyle} color="secondary" text={legend} />
              </Box>
            </SourceContentWrapper>
          )
        )}
      </Col>
      <Col
        span={12}
        style={{
          padding: '0 3px',
          ...(isReferenceTarget
            ? {
                borderInline: dottedBorderStyle,
                borderBottom: isLastField ? dottedBorderStyle : undefined,
              }
            : {}),
        }}
      >
        {target
          ? hasValue && (
              <Box padding="4px 0">
                {isLongField ? (
                  <Flex vertical>
                    <Text
                      {...defaultTextStyle}
                      color="secondary"
                      text={legend}
                    />
                    <Text {...defaultTextStyle} text={targetText} />
                  </Flex>
                ) : (
                  <LegendText
                    {...defaultTextStyle}
                    textJustify={
                      isMonetaryField || isAlignRightField
                        ? 'flex-end'
                        : undefined
                    }
                    legend={legend}
                    text={targetText}
                  />
                )}
              </Box>
            )
          : showLegendAnytime.target && (
              <Box paddingTop={8} paddingBottom={8}>
                <Text {...defaultTextStyle} color="secondary" text={legend} />
              </Box>
            )}
      </Col>
    </>
  );
};
